const val = (v) => (v === null || v === undefined ? '' : String(v).trim());
const pad2 = (n) => String(n).padStart(2, '0');

export function parseDotNetDate(value) {
  const s = val(value);
  if (!s) return null;

  const m = s.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?)?$/i
  );
  if (m) {
    const [, mm, dd, yyyy, hh, min, sec, ampm] = m;
    let hour = hh ? parseInt(hh, 10) : 0;
    const period = (ampm || '').toUpperCase();
    if (period === 'PM' && hour !== 12) hour += 12;
    if (period === 'AM' && hour === 12) hour = 0;
    return new Date(
      parseInt(yyyy, 10), parseInt(mm, 10) - 1, parseInt(dd, 10),
      hour, min ? parseInt(min, 10) : 0, sec ? parseInt(sec, 10) : 0
    );
  }

  const fallback = new Date(s);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

// -> "2026-08-18"
export function otDate(value) {
  const d = parseDotNetDate(value);
  return d ? `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}` : '';
}

// -> "17:10"
export function otTime(value) {
  const d = parseDotNetDate(value);
  return d ? `${pad2(d.getHours())}:${pad2(d.getMinutes())}` : '';
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function otWeekday(value) {
  const d = parseDotNetDate(value);
  return d ? DAY_NAMES[d.getDay()] : '';
}

export function otType(value) {
  return val(value).replace(/\s+/g, ' ').trim().toUpperCase();
}

const OT_TYPE_LABELS = {
  T: 'Normal',
  NORMAL: 'Normal',
  POST_SHIFT: 'Post Shift',
  PRE_SHIFT: 'Pre Shift',
  SUNDAY: 'Sunday',
  HOLIDAY: 'Holiday',
  POYA: 'Poya',
};

export function otTypeLabel(value) {
  const t = otType(value);

  if (!t) return '—';

  if (OT_TYPE_LABELS[t]) {
    return OT_TYPE_LABELS[t];
  }

  // Fallback for unknown values
  return t
    .split(/[\_\s]+/)
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ');
}

export const OT_TYPE_STYLES = {
  POST_SHIFT: 'bg-blue-50 text-blue-700 border-blue-200',
  PRE_SHIFT: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  SUNDAY: 'bg-amber-50 text-amber-700 border-amber-200',
  HOLIDAY: 'bg-red-50 text-red-700 border-red-200',
  POYA: 'bg-violet-50 text-violet-700 border-violet-200',
  NORMAL: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

export const otTypeStyle = (value) =>
  OT_TYPE_STYLES[otType(value)] || 'bg-gray-50 text-gray-600 border-gray-200';

export const num = (v) => {
  const n = parseFloat(val(v));
  return Number.isNaN(n) ? 0 : n;
};

export const isActive = (row) => /^true$/i.test(val(row.HAD_IS_ACTIVE));
export const isOvernight = (row) => num(row.HAD_IS_OVERNIGHT) > 0;

// Normalised row used by the table.
export function mapOTRow(row) {
  return {
    id: val(row.HAD_ID),
    employeeId: val(row.HAD_EMPLOYEE_ID),
    date: otDate(row.HAD_ATTENDANCE_DATE),
    weekday: otWeekday(row.HAD_ATTENDANCE_DATE),
    shiftId: val(row.HAD_SHIFT_ID),
    inTime: otTime(row.HAD_IN_TIME),
    outTime: otTime(row.HAD_OUT_TIME),
    normalHours: num(row.HAD_NORMAL_HOURS),
    otHours: num(row.HAD_OT_HOURS),
    totalHours: row.HAD_TOTAL_HOURS === null || val(row.HAD_TOTAL_HOURS) === ''
      ? num(row.HAD_NORMAL_HOURS) + num(row.HAD_OT_HOURS)
      : num(row.HAD_TOTAL_HOURS),
    otType: otType(row.HAD_OT_TYPE),
    otTypeLabel: otTypeLabel(row.HAD_OT_TYPE),
    otAmount: row.HAD_OT_AMOUNT === null ? null : num(row.HAD_OT_AMOUNT),
    overnight: isOvernight(row),
    active: isActive(row),
    raw: row,
  };
}

export const mapOTRows = (rows, employees = []) => {
  if (!Array.isArray(rows)) return [];

  // Create a map for quick employee lookup
  const employeeMap = new Map();
  employees.forEach(emp => {
    employeeMap.set(emp.employeeId, emp);
  });

  return rows.map((r) => {
    // Parse date safely
    let dateObj = null;
    let formattedDate = '';
    let weekday = '';

    if (r.HOT_OT_DATE) {
      try {
        dateObj = new Date(r.HOT_OT_DATE);
        if (!isNaN(dateObj.getTime())) {
          const y = dateObj.getFullYear();
          const m = String(dateObj.getMonth() + 1).padStart(2, '0');
          const d = String(dateObj.getDate()).padStart(2, '0');
          formattedDate = `${y}/${m}/${d}`;
          weekday = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
        }
      } catch (e) {
        console.warn('Date parsing error:', e);
      }
    }

    // Parse hours
    const normalHours = parseFloat(r.HOT_NORMAL_HOURS) || 0;
    const otHours = parseFloat(r.HOT_TOTAL_HOURS) || 0;
    const otAmount = r.HOT_OT_AMOUNT ? parseFloat(r.HOT_OT_AMOUNT) : null;

    // Get OT type and clean it
    const otType = r.HOT_OT_TYPE ? r.HOT_OT_TYPE.trim() : '';

    // Check if approved
    const isApproved = r.HOT_IS_APPROVED === 'True' || r.HOT_IS_APPROVED === true;

    // Get employee name from the map
    const employeeId = r.HOT_EMPLOYEE_ID || '';
    const employee = employeeMap.get(employeeId);
    const employeeName = employee?.name || '';

    return {
      // Original data for reference
      raw: r,

      // Display fields
      id: r.HOT_OVERTIME_ID || '',
      otId: r.HOT_OVERTIME_ID || '',
      employeeId: employeeId,
      employeeName: employeeName,
      date: formattedDate,
      weekday: weekday,
      shiftId: r.HOT_SHIFT_ID || r.SHIFT_ID || '',
      inTime: r.HOT_START_TIME ? r.HOT_START_TIME.substring(0, 5) : '',
      outTime: r.HOT_END_TIME ? r.HOT_END_TIME.substring(0, 5) : '',

      // Numeric fields
      normalHours: normalHours,
      otHours: otHours,
      totalHours: normalHours + otHours,

      // OT Type
      otType: otType,
      otTypeLabel: otTypeLabel(r.HOT_OT_TYPE),
      // Amount
      otAmount: otAmount,

      // Status
      active: true,
      isApproved: isApproved,
      approvedBy: r.HOT_APPROVED_BY || '',
      approvedDate: r.HOT_APPROVED_DATE || '',
      remarks: r.HOT_REMARKS || '',

      // Additional fields for sorting/filtering
      overtimeId: r.HOT_OVERTIME_ID,
      payrollPeriodId: r.HOT_PAYROLL_PERIOD_ID,
      otRate: parseFloat(r.HOT_OT_RATE) || 0,
      payrollId: r.HOT_PAYROLL_ID || '',
    };
  });
};

const round2 = (n) => Math.round(n * 100) / 100;

export function computeOTKpis(rows) {
  const totalOT = rows.reduce((sum, r) => sum + r.otHours, 0);
  const totalNormal = rows.reduce((sum, r) => sum + r.normalHours, 0);
  const employees = new Set(rows.map((r) => r.employeeId).filter(Boolean));
  const otDays = new Set(rows.map((r) => r.date).filter(Boolean));

  return {
    totalOTHours: round2(totalOT),
    totalNormalHours: round2(totalNormal),
    records: rows.length,
    employees: employees.size,
    otDays: otDays.size,
    avgOTPerRecord: rows.length ? round2(totalOT / rows.length) : 0,
    overnightCount: rows.filter((r) => r.overnight).length,
    inactiveCount: rows.filter((r) => !r.active).length,
  };
}

// OT hours grouped by type — for the breakdown chips / chart.
export function buildOTByType(rows) {
  const map = new Map();
  rows.forEach((r) => {
    const key = r.otType || 'UNSPECIFIED';
    if (!map.has(key)) map.set(key, { type: key, label: r.otTypeLabel, hours: 0, count: 0 });
    const entry = map.get(key);
    entry.hours += r.otHours;
    entry.count += 1;
  });
  return Array.from(map.values())
    .map((e) => ({ ...e, hours: round2(e.hours) }))
    .sort((a, b) => b.hours - a.hours);
}

// OT hours per employee — useful when no employee filter is applied.
export function buildOTByEmployee(rows) {
  const map = new Map();
  rows.forEach((r) => {
    if (!r.employeeId) return;
    if (!map.has(r.employeeId)) {
      map.set(r.employeeId, { employeeId: r.employeeId, hours: 0, days: new Set() });
    }
    const entry = map.get(r.employeeId);
    entry.hours += r.otHours;
    if (r.date) entry.days.add(r.date);
  });
  return Array.from(map.values())
    .map((e) => ({ employeeId: e.employeeId, hours: round2(e.hours), days: e.days.size }))
    .sort((a, b) => b.hours - a.hours);
}

// Daily OT trend, oldest first.
export function buildOTTrend(rows) {
  const map = new Map();
  rows.forEach((r) => {
    if (!r.date) return;
    map.set(r.date, round2((map.get(r.date) || 0) + r.otHours));
  });
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, hours]) => ({ date, label: date.slice(5), hours }));
}

// Decimal hours -> "1h 08m"
export function formatOTHours(hours) {
  const total = Math.round((Number(hours) || 0) * 60);
  return `${Math.floor(total / 60)}h ${pad2(total % 60)}m`;
}

// ── Extra aggregations for the OT Dashboard ────────────────────────────────

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// OT hours split by weekday — surfaces which days OT concentrates on.
export function buildOTByWeekday(rows) {
  const order = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const map = new Map(order.map((d) => [d, { day: d, short: d.slice(0, 3), hours: 0, count: 0 }]));
  rows.forEach((r) => {
    if (!r.weekday || !map.has(r.weekday)) return;
    const e = map.get(r.weekday);
    e.hours += r.otHours;
    e.count += 1;
  });
  return order.map((d) => {
    const e = map.get(d);
    return { ...e, hours: Math.round(e.hours * 100) / 100 };
  });
}

// OT hours per calendar month, oldest first.
export function buildOTByMonth(rows) {
  const map = new Map();
  rows.forEach((r) => {
    if (!r.date) return;
    const key = r.date.slice(0, 7); // yyyy-mm
    if (!map.has(key)) map.set(key, { month: key, hours: 0, count: 0 });
    const e = map.get(key);
    e.hours += r.otHours;
    e.count += 1;
  });
  return Array.from(map.values())
    .sort((a, b) => a.month.localeCompare(b.month))
    .map((e) => ({
      ...e,
      hours: Math.round(e.hours * 100) / 100,
      label: `${MONTH_LABELS[parseInt(e.month.slice(5), 10) - 1]} ${e.month.slice(0, 4)}`,
    }));
}

// Normal vs OT hours — the donut showing how much of total time is overtime.
export function buildNormalVsOT(rows) {
  const normal = rows.reduce((s, r) => s + r.normalHours, 0);
  const ot = rows.reduce((s, r) => s + r.otHours, 0);
  return [
    { name: 'Normal Hours', value: Math.round(normal * 100) / 100, color: '#3b82f6' },
    { name: 'OT Hours', value: Math.round(ot * 100) / 100, color: '#10b981' },
  ];
}

// Donut-friendly version of the type breakdown, with fixed colours per type.
const TYPE_COLORS = {
  POST_SHIFT: '#3b82f6',
  PRE_SHIFT: '#6366f1',
  SUNDAY: '#f59e0b',
  HOLIDAY: '#ef4444',
  POYA: '#8b5cf6',
  NORMAL: '#10b981',
};
const FALLBACK_COLORS = ['#14b8a6', '#f97316', '#ec4899', '#64748b', '#84cc16'];

export function buildOTTypeDonut(rows) {
  const map = new Map();
  rows.forEach((r) => {
    const key = r.otType || 'UNSPECIFIED';
    if (!map.has(key)) map.set(key, { type: key, name: r.otTypeLabel || 'Unspecified', value: 0, count: 0 });
    const e = map.get(key);
    e.value += r.otHours;
    e.count += 1;
  });
  return Array.from(map.values())
    .sort((a, b) => b.value - a.value)
    .map((e, i) => ({
      ...e,
      value: Math.round(e.value * 100) / 100,
      color: TYPE_COLORS[e.type] || FALLBACK_COLORS[i % FALLBACK_COLORS.length],
    }));
}

// Buckets OT records by size, so you can see how many are short vs long stints.
export function buildOTHourBuckets(rows) {
  const buckets = [
    { label: '0 – 1h', min: 0, max: 1, count: 0 },
    { label: '1 – 2h', min: 1, max: 2, count: 0 },
    { label: '2 – 4h', min: 2, max: 4, count: 0 },
    { label: '4 – 8h', min: 4, max: 8, count: 0 },
    { label: '8h +', min: 8, max: Infinity, count: 0 },
  ];
  rows.forEach((r) => {
    const b = buckets.find((x) => r.otHours > x.min && r.otHours <= x.max)
      || (r.otHours === 0 ? buckets[0] : null);
    if (b) b.count += 1;
  });
  return buckets.map(({ label, count }) => ({ label, count }));
}